import type { MigrationStore, StoredEnvelope } from '../migrations/types';
import type { ErasureOutcome, KeyValueStore, StorageBackend } from './key-value-store';
import { QUARANTINE_PREFIX } from './keys';

/**
 * JSON layer over the key/value store.
 *
 * Every value is wrapped in a versioned envelope, and every read is checked three ways before
 * the caller sees it: the text must parse, the envelope must carry a supported schema version,
 * and the payload must still match the shape its owner expects.
 *
 * Two rules follow from the privacy invariant (architecture §19), and both are why this file is
 * more than a `JSON.parse` wrapper:
 *
 *   1. A payload that only *partly* survives projection is rewritten in place with the
 *      sanitised value. Returning a clean object while leaving camera frames sitting under the
 *      original key would satisfy the type system and defeat the invariant. When the rewrite
 *      cannot be persisted, the original is *erased* instead: losing a legitimate record beats
 *      retaining forbidden bytes (D-029).
 *   2. A rejected record is never copied anywhere, and nothing *derived from its content* is
 *      kept either. Quarantine holds fixed diagnostic categories only — which key, which
 *      failure category, the version the envelope claimed, when it happened, and what erasure
 *      physically achieved. Nothing in the record varies with the payload's text, its length or
 *      its shape, so two rejected records differ only by key and timestamp.
 *
 * Both paths destroy the stored bytes through the same primitive, `KeyValueStore.erase`, which
 * attempts deletion and tombstone-overwrite physically rather than asking capability flags for
 * permission (review R4-1/R4-3/R4-4).
 */

export type { StoredEnvelope };

/**
 * Projects a stored payload into its domain shape, or returns null if the record is no longer
 * usable. Supplied by the repository that owns the key, so validation lives with the type it
 * describes rather than in a central table that can drift.
 */
export type PayloadProjector<T> = (data: unknown) => T | null;

/** Why a record was rejected. A closed set of categories, never a description of the value. */
export type QuarantineReason =
  | 'unparseable'
  | 'not-an-envelope'
  | 'non-integer-schema-version'
  | 'schema-version-too-new'
  | 'schema-version-too-old'
  | 'payload-shape-mismatch';

/**
 * What is left behind when a record is rejected.
 *
 * Every field is chosen by this code (`key`, `reason`, `erasureOutcome`), read from the clock
 * (`quarantinedAt`), or a small integer the envelope declared about itself (`schemaVersion`).
 * Nothing is derived from the payload.
 *
 * Field names, content digests, `fieldCount` and `byteLength` have all been removed in turn,
 * each for the same reason: a top-level key is attacker-controlled text, a digest plus a length
 * is a dictionary oracle, and a bare length or entry count still identifies a payload whose
 * size is unique among the candidates — an array of movement samples announces how many it
 * held (review R3-2, R4-2). Two rejected records now differ only by `key` and `quarantinedAt`.
 */
export interface QuarantineRecord {
  /** The storage key, which this build chose — never anything the payload supplied. */
  key: string;
  reason: QuarantineReason;
  /** The version the envelope claimed, or -1 when it did not claim a usable one. */
  schemaVersion: number;
  quarantinedAt: string;
  /** What physically happened to the rejected bytes. */
  erasureOutcome: ErasureOutcome;
}

const QUARANTINE_REASONS: readonly QuarantineReason[] = [
  'unparseable',
  'not-an-envelope',
  'non-integer-schema-version',
  'schema-version-too-new',
  'schema-version-too-old',
  'payload-shape-mismatch',
];

const ERASURE_OUTCOMES: readonly ErasureOutcome[] = ['removed', 'overwritten', 'failed'];

const QUARANTINE_FIELDS = [
  'erasureOutcome',
  'key',
  'quarantinedAt',
  'reason',
  'schemaVersion',
] as const;

/**
 * The one way this build spells a quarantine record.
 *
 * Both the writer and the validator go through here, and the validator demands byte equality with
 * it. That is the point: validating the *parsed* object accepted raw text that `JSON.parse` had
 * quietly normalised away — duplicate members collapse to the last one, so
 * `{"key":"<forbidden>","key":"thrive.v1.progress",…}` parsed as an impeccable five-field
 * diagnostic while the stored string kept the forbidden bytes forever (review R7-4). Whitespace,
 * escapes and member order are the same class of hiding place. A record that is not character-
 * for-character what this build would have written is not this build's record.
 */
export function serialiseQuarantineRecord(record: QuarantineRecord): string {
  return JSON.stringify({
    key: record.key,
    reason: record.reason,
    schemaVersion: record.schemaVersion,
    quarantinedAt: record.quarantinedAt,
    erasureOutcome: record.erasureOutcome,
  });
}

/**
 * An instant this build's clock could have produced, spelled the one way `Date` spells it.
 *
 * `typeof x === 'string'` on a timestamp field is not a check, it is a hole: any bytes at all
 * fit through it (review R6-4). Requiring the value to survive a round trip through `Date`
 * unchanged reduces the field to 24 characters of digits and punctuation with no room for a
 * payload to hide in.
 */
function isCanonicalIsoInstant(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value;
}

/**
 * True only for a diagnostic in exactly the shape this build writes, filed against `expectedKey`.
 *
 * The set of fields a quarantine record may hold has shrunk twice as each one turned out to be a
 * payload oracle (D-028 → D-029). A record left behind by an older build therefore carries data
 * this build has decided must not exist, and nothing ever reads it — `keys()` hides the prefix
 * and the repositories never look. The decontamination pass uses this to find them and erase them
 * outright rather than parse and preserve them: a stale diagnostic has no value worth the risk
 * (review R5-2). Anything unrecognised — including a *newer* build's shape — is rejected, which
 * is deliberate; diagnostics are disposable and a false negative here means retained bytes.
 *
 * Every field is now pinned to something this build controls. `key` must be the key the record
 * is filed under, and `quarantinedAt` must be a canonical instant: both used to accept arbitrary
 * strings, which made them the two places forbidden bytes could sit inside an otherwise
 * impeccable diagnostic and survive every launch (review R6-4).
 */
export function isCurrentQuarantineRecord(
  value: unknown,
  expectedKey: string,
): value is QuarantineRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;

  const record = value as Record<string, unknown>;
  if (Object.keys(record).sort().join(',') !== QUARANTINE_FIELDS.join(',')) return false;

  return (
    record['key'] === expectedKey &&
    QUARANTINE_REASONS.includes(record['reason'] as QuarantineReason) &&
    typeof record['schemaVersion'] === 'number' &&
    Number.isSafeInteger(record['schemaVersion']) &&
    isCanonicalIsoInstant(record['quarantinedAt']) &&
    ERASURE_OUTCOMES.includes(record['erasureOutcome'] as ErasureOutcome)
  );
}

/**
 * True only for **stored text** that is exactly what this build writes for `expectedKey`.
 *
 * Field validation on the parsed object is necessary and not sufficient: the parse is lossy, and
 * everything it loses stays on disk. So the text must first survive every field rule, and then be
 * byte-identical to this build's canonical serialisation of the record it parsed to (R7-4).
 * Anything else is erased — a diagnostic is disposable, and a false negative here costs one
 * re-derivable record while a false positive keeps forbidden bytes forever.
 */
export function isCurrentQuarantineText(raw: string, expectedKey: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Unparseable text under a diagnostic key is not a diagnostic.
    return false;
  }

  return (
    isCurrentQuarantineRecord(parsed, expectedKey) && raw === serialiseQuarantineRecord(parsed)
  );
}

/**
 * A version is only recorded when it is a small whole number this build could have written.
 *
 * `Number.isFinite` alone would admit `1.0000001234567`, and a fractional version is enough
 * bits to smuggle payload data back out through the diagnostic.
 */
function recordableVersion(value: unknown): number {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : -1;
}

/**
 * A single-slot record's write or clear could not be made durable (S1-5).
 *
 * Thrown rather than swallowed because the caller's decision depends on it: a sitting whose
 * records could not be replaced or cleared is one whose *stale* pair is still physically there,
 * and the shell says so out loud instead of letting the next launch resume somebody else's
 * mission. Carries no value and no bytes — only which key and what the erasure managed.
 */
export class StorageSlotError extends Error {
  readonly key: string;
  readonly operation: 'replace' | 'clear';
  readonly erasure: ErasureOutcome;

  constructor(key: string, operation: 'replace' | 'clear', erasure: ErasureOutcome) {
    super(`Storage could not ${operation} "${key}" (erasure: ${erasure}).`);
    this.name = 'StorageSlotError';
    this.key = key;
    this.operation = operation;
    this.erasure = erasure;
  }
}

export interface JsonStore extends MigrationStore {
  readonly backend: StorageBackend;
  read<T>(key: string, project?: PayloadProjector<T>): T | null;
  write<T>(key: string, value: T): void;
  /**
   * Writes a record whose stale copy must never outlive a failed write (S1-5).
   *
   * For the single-slot sitting records — the mission snapshot and the launch identity — whose
   * mere presence decides whether a saved mission is offered to the child at the screen. On
   * failure the superseded bytes are erased and a `StorageSlotError` is thrown.
   */
  replaceSlot<T>(key: string, value: T): void;
  /** Clears a single-slot record with verified removal; throws when it cannot be proven gone. */
  clearSlot(key: string): void;
  remove(key: string): void;
  keys(): string[];
  readEnvelope(key: string): StoredEnvelope<unknown> | null;
  /**
   * Runs `fn` with **both** deferral counters held — this store's warnings and the key/value
   * store's warnings and backend notifications — so nothing application-supplied runs until it
   * returns.
   *
   * Each store method already opens this scope for itself, and nesting is free. What it exists
   * for is a caller that needs a *multi-key* sequence to be uninterruptible: the decontamination
   * pass touches every key in the namespace, and this store's counter used to return to zero
   * between them, flushing warnings mid-pass and giving a callback the chance to disable the
   * backend before the later keys had even been read (review R6-2).
   */
  withGovernedScope<T>(fn: () => T): T;
  /**
   * Validates the bytes **physically stored** under `key` and destroys what must not exist.
   *
   * The read path's twin, with three deliberate differences. It reads through `readPhysical`, so
   * a session overlay cannot hide the stored copy from it (R6-3); a read that *fails* is erased
   * rather than treated as an empty key (R7-1); and it returns nothing, because its job is the
   * bytes on disk rather than a value for a caller. A record that is clean costs
   * exactly one read: the rewrite only happens when projection changed something, which is what
   * keeps a healthy relaunch write-free (R3-8).
   */
  decontaminate(key: string, project: PayloadProjector<unknown>): void;
}

export interface JsonStoreOptions {
  /** The version this build writes, and the only one `read` will hand to a projector. */
  schemaVersion: number;
  /**
   * Oldest version the migration layer can still make sense of. Envelopes at or above this and
   * below `schemaVersion` are left intact for migrations; anything else is quarantined.
   */
  minimumSupportedVersion: number;
  onWarning?: (message: string, cause?: unknown) => void;
}

function isEnvelope(value: unknown): value is StoredEnvelope<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'data' in value &&
    typeof (value as { schemaVersion?: unknown }).schemaVersion === 'number'
  );
}

export function createJsonStore(store: KeyValueStore, options: JsonStoreOptions): JsonStore {
  const emitWarning =
    options.onWarning ??
    ((message: string, cause?: unknown) => {
      console.warn(`[thrive-storage] ${message}`, cause);
    });

  /**
   * This store's own warnings are application callbacks too, and they are deferred for the same
   * reason the key/value store defers its own (review R5-1, SC-1): a governed operation is often
   * a *sequence* — write, discover it failed, erase — and a handler that throws part-way through
   * used to abandon the rest of it with forbidden bytes still on disk.
   */
  let warningDepth = 0;
  const deferredWarnings: { message: string; cause?: unknown }[] = [];

  const warn = (message: string, cause?: unknown): void => {
    if (warningDepth > 0) {
      deferredWarnings.push(cause === undefined ? { message } : { message, cause });
      return;
    }
    emitWarning(message, cause);
  };

  /**
   * Runs a governed operation with no callback of any kind able to interleave with it.
   *
   * The key/value store's scope covers its own warnings and backend notifications; the loop here
   * covers this store's. Each queued warning is isolated so one throwing handler cannot cancel
   * the diagnostics behind it.
   */
  const governed = <T>(operation: () => T): T => {
    warningDepth += 1;
    try {
      return store.withDeferredCallbacks(operation);
    } finally {
      warningDepth -= 1;
      if (warningDepth === 0) {
        for (const entry of deferredWarnings.splice(0, deferredWarnings.length)) {
          try {
            emitWarning(entry.message, entry.cause);
          } catch {
            // Deliberately ignored: the only channel for reporting it is the one that threw.
          }
        }
      }
    }
  };

  /**
   * Destroys a rejected record, then records a diagnostic about the rejection.
   *
   * Erasure comes first and unconditionally. Writing the diagnostic can fail, can degrade the
   * backend, and — through `onWarning` — can run arbitrary application code; none of that may
   * happen while forbidden bytes are still on disk.
   */
  const quarantine = (key: string, parsed: unknown, reason: QuarantineReason): void => {
    const erasureOutcome = store.erase(key);

    const record: QuarantineRecord = {
      key,
      reason,
      schemaVersion: isEnvelope(parsed) ? recordableVersion(parsed.schemaVersion) : -1,
      quarantinedAt: new Date().toISOString(),
      erasureOutcome,
    };

    // The writer and the validator share one serialiser, so "this build's handwriting" cannot
    // drift apart between the two of them (R7-4).
    const recorded = store.setRaw(`${QUARANTINE_PREFIX}${key}`, serialiseQuarantineRecord(record));

    warn(`Stored value at "${key}" was rejected (${reason}); treating the key as absent.`);
    if (!recorded) {
      warn(`The diagnostic for "${key}" could not be saved; it is only in memory this session.`);
    }
    if (erasureOutcome === 'failed') {
      warn(
        `The rejected value at "${key}" could not be deleted or overwritten; erasure will be re-attempted.`,
      );
    }
  };

  type WriteOutcome = 'persisted' | 'memory-only' | 'unserialisable';

  const writeEnvelope = <T>(key: string, value: T): WriteOutcome => {
    const envelope: StoredEnvelope<T> = { schemaVersion: options.schemaVersion, data: value };
    let serialised: string;
    try {
      serialised = JSON.stringify(envelope);
    } catch (cause) {
      // Circular or non-serialisable data is a programming error, not a storage failure.
      warn(`Could not serialise the value for "${key}"; nothing was written.`, cause);
      return 'unserialisable';
    }
    return store.setRaw(key, serialised) ? 'persisted' : 'memory-only';
  };

  /**
   * Parses and version-checks a value already fetched from somewhere.
   *
   * Takes the bytes rather than the key so the read path and the decontamination path can share
   * every rejection rule while disagreeing about *where* the bytes come from — the session view
   * or the physical one. A second copy of these rules would drift, and the drift would show up
   * as a record one path waves through and the other destroys.
   */
  const parseRaw = (key: string, raw: string | null): StoredEnvelope<unknown> | null => {
    if (raw === null) return null;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Unparseable text cannot be projected, migrated or usefully described — and it can still
      // be a camera frame that a truncated write left behind, so it is erased like any other
      // rejected record rather than merely deleted where deletion happens to work (R4-3).
      quarantine(key, undefined, 'unparseable');
      return null;
    }

    if (!isEnvelope(parsed)) {
      quarantine(key, parsed, 'not-an-envelope');
      return null;
    }

    if (!Number.isInteger(parsed.schemaVersion)) {
      quarantine(key, parsed, 'non-integer-schema-version');
      return null;
    }

    if (parsed.schemaVersion > options.schemaVersion) {
      // Written by a newer build. This one cannot know what the fields mean.
      quarantine(key, parsed, 'schema-version-too-new');
      return null;
    }

    if (parsed.schemaVersion < options.minimumSupportedVersion) {
      quarantine(key, parsed, 'schema-version-too-old');
      return null;
    }

    return parsed;
  };

  /** The session view. What `read`, `readEnvelope` and the repositories see. */
  const parseEnvelope = (key: string): StoredEnvelope<unknown> | null =>
    parseRaw(key, store.getRaw(key));

  /**
   * Projection as a privacy boundary: if it removed or changed anything, the stored copy is
   * rewritten so the discarded bytes stop existing — and when the rewrite cannot be persisted,
   * the stored copy is erased instead.
   *
   * Shared by the read path and the decontamination path so the erasure-wins rule (D-029) is
   * applied identically wherever a projection happens, including on `meta` (review R6-5).
   * Returns the projected value, or null when the record was rejected outright.
   */
  const sanitise = <T>(
    key: string,
    envelope: StoredEnvelope<unknown>,
    project: PayloadProjector<T>,
  ): T | null => {
    const projected = project(envelope.data);
    if (projected === null) {
      quarantine(key, envelope, 'payload-shape-mismatch');
      return null;
    }

    if (JSON.stringify(projected) === JSON.stringify(envelope.data)) return projected;

    const outcome = writeEnvelope(key, projected);
    if (outcome === 'persisted') {
      warn(`Stored value at "${key}" contained fields outside its schema; rewriting it clean.`);
    } else {
      // The clean rewrite could not reach the backend, so the contaminated original is
      // still down there under a tidy in-memory overlay. Erasing it costs a legitimate
      // record on the next launch; keeping it would keep camera bytes on disk, and the
      // privacy invariant is absolute (D-029, review R4-1). The overlay keeps serving the
      // sanitised value for the rest of this session.
      const erasure = store.erase(key, { retainSessionValue: true });
      warn(
        `Stored value at "${key}" contained fields outside its schema and the clean rewrite could not be saved; the stored copy has been erased (${erasure}).`,
      );
    }

    return projected;
  };

  /**
   * The application's read path: version gate, projection, and the sanitising rewrite.
   *
   * Callers reach it through `read`, which runs it inside a governed scope — every branch below
   * can write, erase, or both, and none of them may be interrupted part-way by a callback.
   */
  const readGoverned = <T>(key: string, project?: PayloadProjector<T>): T | null => {
    const envelope = parseEnvelope(key);
    if (envelope === null) return null;

    if (envelope.schemaVersion !== options.schemaVersion) {
      // Supported, but not upgraded. Migrations run before any repository read, so this means
      // no migration claimed it: leave it intact for a future one rather than destroying it,
      // and report absence to the caller.
      warn(
        `Stored value at "${key}" is still at schema version ${envelope.schemaVersion}; no migration has upgraded it, so it is being ignored rather than used.`,
      );
      return null;
    }

    if (project === undefined) return envelope.data as T;

    return sanitise(key, envelope, project);
  };

  /** The decontamination path: identical rules, physical bytes, no value handed back. */
  const decontaminateGoverned = (key: string, project: PayloadProjector<unknown>): void => {
    const physical = store.readPhysical(key);

    if (physical.status === 'unreadable') {
      // A read that failed is not a key that is empty (R7-1). Whatever is under there could not
      // be inspected, so it is destroyed on the same erasure-wins rule that governs a record
      // whose clean rewrite could not be saved (D-029): the primitive never consults capability
      // conclusions, so it tries even though the read just degraded them, and an outright
      // failure lands in `pendingErasures` for this session's retries and the next launch.
      const outcome = store.erase(key);
      warn(
        `The stored bytes at "${key}" could not be read, so the key was erased rather than assumed absent (${outcome}).`,
      );
      return;
    }

    const envelope = parseRaw(key, physical.status === 'present' ? physical.value : null);
    if (envelope === null) return;

    // Supported but older: a migration owns this record and destroying it here would destroy
    // data the app is about to upgrade. The pass runs again after the migration chain applies,
    // which is where such a record gets projected at the version it was upgraded to.
    if (envelope.schemaVersion !== options.schemaVersion) return;

    sanitise(key, envelope, project);
  };

  return {
    get backend() {
      return store.backend;
    },

    /**
     * The migration layer's view: a supported *older* envelope is handed over intact rather
     * than quarantined, because upgrading it is exactly what the caller is about to do.
     */
    readEnvelope(key: string): StoredEnvelope<unknown> | null {
      // A read can quarantine, and a quarantine erases, so it is a governed operation.
      return governed(() => parseEnvelope(key));
    },

    read<T>(key: string, project?: PayloadProjector<T>): T | null {
      return governed(() => readGoverned(key, project));
    },

    withGovernedScope<T>(fn: () => T): T {
      return governed(fn);
    },

    decontaminate(key: string, project: PayloadProjector<unknown>): void {
      governed(() => {
        decontaminateGoverned(key, project);
      });
    },

    write<T>(key: string, value: T): void {
      governed(() => writeEnvelope(key, value));
    },

    replaceSlot<T>(key: string, value: T): void {
      governed(() => {
        if (writeEnvelope(key, value) === 'persisted') return;

        /**
         * A store with no browser backend at all has nothing underneath the overlay: there are no
         * superseded bytes to resurrect, so "memory-only" here is the whole truth rather than a
         * lie told on top of a stale record. Nothing to erase, nothing to report.
         *
         * Asked as "was there ever a handle", never as "is the backend healthy" (S1-5 round 2).
         * `backend` is a conclusion, and a single transient read failure at startup collapses it
         * to `memory` while the previous sitting's pair is still physically stored — skipping the
         * erasure on that basis leaves exactly the stale pair this method exists to destroy. The
         * erasure primitive itself never consults capability conclusions either (D-032(1)), so it
         * still tries when they say it cannot.
         */
        if (!store.hasPhysicalStore) return;

        /**
         * The replacement is only in the overlay, and the record it was meant to supersede is
         * still physically underneath it (S1-5). For an append-only table that is merely a lost
         * update; for a single-slot sitting record it is a different sitting coming back after a
         * reload and passing `resolveSitting` — child A's coherent identity/snapshot pair
         * resurrected while child B is at the screen.
         *
         * So the superseded bytes are destroyed instead, with `retainSessionValue` so this
         * session keeps playing on the overlay. The cost is the old restart-on-refresh behaviour
         * (D-055(3)); the alternative is misattributing a child's mission, which D-036(4) exists
         * to prevent.
         */
        const erasure = store.erase(key, { requireRemoval: true, retainSessionValue: true });
        warn(
          `"${key}" could not be saved, so the record it replaces has been erased (${erasure}); this sitting will not be resumable.`,
        );
        throw new StorageSlotError(key, 'replace', erasure);
      });
    },

    clearSlot(key: string): void {
      governed(() => {
        // A tombstone overwrite is not enough for a record whose *presence* is the decision: a
        // cleared sitting that comes back after a reload is a mission handed to whoever launches
        // next. Only a verified removal discharges it (the D-032(3)/D-033(1) `requireRemoval`
        // rule, applied to an ordinary clear); anything else stays in `pendingErasures` and is
        // reported to the caller rather than swallowed.
        const outcome = store.erase(key, { requireRemoval: true });
        if (outcome === 'removed') return;
        warn(`"${key}" could not be cleared (${outcome}); it will be re-attempted.`);
        throw new StorageSlotError(key, 'clear', outcome);
      });
    },

    remove(key: string): void {
      governed(() => store.remove(key));
    },

    keys(): string[] {
      // Quarantine diagnostics are evidence, not live data: migrations must not walk them.
      return governed(() => store.keys().filter((key) => !key.startsWith(QUARANTINE_PREFIX)));
    },
  };
}
